import TableColumn from './TableColumn.js';
import TableColumns from './TableColumns.js';
import ColumnSet from './ColumnSet.js';
import {HTMLSanitizer} from "../../util/CommonUtils.js";

/**
 * 2. Set LargestPeak flag
 * 3. Go through each chromatogram and calculate number of peaks
 * 4. Generate column names and expressions for N of peaks and LargestPeak
 * 5. Generate Injection and Structure-level column names
 */
export default class TableColumnsFactory {
    /**
     * @param {InjectionRow[]} injections
     * @returns {TableColumns}
     */
    static createInjectionTableColumns(injections) {
        const columns = new ColumnSet();
        columns.putAll(TableColumnsFactory._createInjectionColumns());
        for (const injection of injections) {
            columns.putAll(TableColumnsFactory._createSubstanceColumns(injection.substances))
            columns.putAll(TableColumnsFactory.createPeakColumns(injection.peaks))
            columns.putAll(TableColumnsFactory.createUnknownPeakColumns(injection.unknownPeaks))
            columns.putAll(TableColumnsFactory.createChromatogramColumns(injection.chromatograms))
            columns.putAll(TableColumnsFactory.createUserDefinedPropertiesColumns(injection.injection.prop))
        }
        return new TableColumns(columns.getSorted());
    }

    /**
     * @returns {TableColumn[]}
     */
    static _createInjectionColumns() {
        return [
            new TableColumn("Injection", 'injection.name'),
            new TableColumn("Plate", 'injection.plateId'),
            new TableColumn("Plate location", 'injection.well'),
            new TableColumn("Plate column", 'injection.pos.colNumber', {type: 'number'}),
            new TableColumn("Plate row", 'injection.pos.rowNumber', {type: 'number'}),
            new TableColumn("Inlet method", 'injection.inletMethod'),
            new TableColumn("Detector method", 'injection.detectorMethod'),
            new TableColumn("Instrument name", 'injection.instrumentName'),
            new TableColumn("Run time min", 'injection.duration', {type:"number"}),
            new TableColumn("Acquired time", 'injection.acquiredTime'),
            new TableColumn("Run", 'injection.run')
        ]
    }

    /**
     * @param {PivotedPeak[]} peaks
     * @returns {TableColumn[]}
     * @private
     */
    static createPeakColumns(peaks) {
        const columns = new ColumnSet();
        const peaksPerChromatogram = {};
        for (let peak of peaks) {
            let substanceName = peak.substance.displayName;
            let chromatogramName = HTMLSanitizer.escapeQuotes(peak.chromatogram.name);
            let basename = `${chromatogramName}`;
            if (!peak.chromatogram.isMs() || peak.chromatogram.isTotal)
                basename += ' ' + substanceName;
            if (peaksPerChromatogram[basename] == null){
                peaksPerChromatogram[basename] = {
                    expression: `[peaks[.chromatogram.name == '${chromatogramName}' && .substance.displayName == '${substanceName}']].length`,
                    peaks: 1
                };
            }
            let peakNumber = peaksPerChromatogram[basename].peaks++;
            const peakName = `${basename} Peak${peakNumber} `;
            const peakExpression = `peaks[.chromatogram.name == '${chromatogramName}' && .substance.displayName == '${substanceName}'][${peakNumber-1}]`
            columns.put(new TableColumn(peakName + 'RT min', peakExpression +'.rt', {type:"number"}));
            columns.put(new TableColumn(peakName + 'Area', peakExpression +'.area', {type:"number"}));
            columns.put(new TableColumn(peakName + 'Area%', peakExpression +'.areaPercent', {type:"number"}));
            columns.put(new TableColumn(peakName + 'Start min', peakExpression + '.start', {type:"number"}));
            columns.put(new TableColumn(peakName + 'End min', peakExpression + '.end', {type:"number"}));
            columns.put(new TableColumn(peakName + 'Base', peakExpression+'.base', {type:"number"}));
            columns.put(new TableColumn(peakName + 'IsManual', peakExpression+'.isManual', {type:"boolean", title: "Whether the peak was detected automatically or was selected/modified manually"}));
            if (peak.largestPeak) {
                const expr = `peaks[.chromatogram.name == '${chromatogramName}' && .substance.displayName == '${substanceName}' && .largestPeak == ${true}][0]`
                columns.put(new TableColumn(`${basename} LargestPeak RT min`, expr + '.rt', {type:"number"}));
                columns.put(new TableColumn(`${basename} LargestPeak Area`, expr + '.area', {type:"number"}));
                columns.put(new TableColumn(`${basename} LargestPeak Area%`, expr + '.areaPercent', {type:"number"}));
                columns.put(new TableColumn(`${basename} LargestPeak Start min`, expr + '.start', {type:"number"}));
                columns.put(new TableColumn(`${basename} LargestPeak End min`, expr + '.end', {type:"number"}));
                columns.put(new TableColumn(`${basename} LargestPeak Base`, expr + '.base', {type:"number"}));
                columns.put(new TableColumn(`${basename} LargestPeak IsManual`, peakExpression+'.isManual', {type:"boolean", title: "Whether the peak was detected automatically or was selected/modified manually"}));
            }
        }
        for (const key in peaksPerChromatogram) {
            columns.put(new TableColumn(key + ' Peak cnt', peaksPerChromatogram[key].expression, {type: "number"}));
        }
        columns.put(new TableColumn('Peak cnt', 'peaksCount', {type:"number", title: "The number of peaks in an injection"}));
        return columns.getSorted();
    }
    /**
     * @param {PivotedPeak[]} peaks
     * @returns {TableColumn[]}
     * @private
     */
    static createUnknownPeakColumns(peaks) {
        const columns = new ColumnSet();
        const peaksPerChromatogram = {};
        for (let peak of peaks) {
            let chromatogramName = HTMLSanitizer.escapeQuotes(peak.chromatogram.name);
            let basename = `${chromatogramName}`;
            if (peaksPerChromatogram[basename] == null){
                peaksPerChromatogram[basename] = {
                    expression: `[unknownPeaks[.chromatogram.name == '${chromatogramName}' && .isUnknown == ${true}]].length`,
                    peaks: 1
                };
            }
            let peakNumber = peaksPerChromatogram[basename].peaks++;
            const peakName = `${basename} UnknownPeak${peakNumber} `;
            const peakExpression = `unknownPeaks[.chromatogram.name == '${chromatogramName}' && .isUnknown == ${true}][${peakNumber-1}]`
            columns.put(new TableColumn(peakName + 'RT min', peakExpression +'.rt', {type:"number"}));
            columns.put(new TableColumn(peakName + 'Area', peakExpression +'.area', {type:"number"}));
            columns.put(new TableColumn(peakName + 'Area%', peakExpression +'.areaPercent', {type:"number"}));
            columns.put(new TableColumn(peakName + 'Start min', peakExpression + '.start', {type:"number"}));
            columns.put(new TableColumn(peakName + 'End min', peakExpression + '.end', {type:"number"}));
            columns.put(new TableColumn(peakName + 'Base', peakExpression+'.base', {type:"number"}));
            if (peak.largestPeak) {
                const expr = `unknownPeaks[.chromatogram.name == '${chromatogramName}' && .isUnknown == ${true} && .largestPeak == ${true}][0]`
                columns.put(new TableColumn(`${basename} LargestUnknownPeak RT min`, expr + '.rt', {type:"number"}));
                columns.put(new TableColumn(`${basename} LargestUnknownPeak Area`, expr + '.area', {type:"number"}));
                columns.put(new TableColumn(`${basename} LargestUnknownPeak Area%`, expr + '.areaPercent', {type:"number"}));
                columns.put(new TableColumn(`${basename} LargestUnknownPeak Start min`, expr + '.start', {type:"number"}));
                columns.put(new TableColumn(`${basename} LargestUnknownPeak End min`, expr + '.end', {type:"number"}));
                columns.put(new TableColumn(`${basename} LargestUnknownPeak Base`, expr + '.base', {type:"number"}));
            }
        }
        for (const key in peaksPerChromatogram) {
            columns.put(new TableColumn(key + ' UnknownPeak cnt', peaksPerChromatogram[key].expression, {type: "number"}));
        }
        columns.put(new TableColumn('UnknownPeak cnt', 'unknownPeaksCount', {type:"number", title: "The number of unknown peaks in an injection"}));
        return columns.getSorted();
    }

    /**
     * @returns {TableColumn[]}
     * @private
     */
    static _createSubstanceColumns(substances) {
        const columns = new ColumnSet();
        for (const substance of substances) {
            const substanceExpression = `substances[.displayName == '${substance.displayName}'][0]`;
            columns.put(new TableColumn(`${substance.displayName} MF`, substanceExpression + '.mf', {type: "string"}));
            columns.put(new TableColumn(`${substance.displayName} EMW`, substanceExpression + '.emw', {type:"number"}));
            columns.put(new TableColumn(`${substance.displayName} struct`, substanceExpression + '.structure', {type:"svg"}));
        }
        return columns.getSorted();
    }

    static createChromatogramColumns(chromatograms) {
        const columns = new ColumnSet();
        for (const chromatogram of chromatograms) {
            const chromName = HTMLSanitizer.escapeQuotes(chromatogram.name);
            columns.put(new TableColumn(chromatogram.name + ' Area', `chromatograms[.name == '${chromName}'][0].area`, {type: "number"}));
        }
        return columns.getSorted();
    }

    /**
     * @param {InjectionProperty[]} injProps
     * @returns {TableColumn[]}
     */
    static createUserDefinedPropertiesColumns(injProps) {
        const columns = new ColumnSet();
        for (const key in injProps) {
            const keyName = HTMLSanitizer.escapeQuotes(key)
            columns.put(new TableColumn(`Injection: ${keyName}`, `injection.prop['${keyName}']`,
                {type: "string", customProp: true}));
        }
        return columns.getSorted();
    }
}